import { motion } from 'framer-motion';
import {
  MessageCircle,
  Heart,
  Star,
  Quote,
  ArrowRight,
  Sparkles,
  Users,
  TrendingUp
} from 'lucide-react';
import Link from '../Link';
import { useEffect, useMemo, useState } from 'react';
import { getAllUsers } from '../../api/authService';
import { getAllComments } from '../../api/adminService';


const TestimonialsSection = () => {

  const [allUsers, setAllUsers] = useState([])
  const [comments, setComments] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      const users = await getAllUsers();
      if (users) {
        setAllUsers(users);
      }
    };
    const fetchComments = async () => {
      const all: any = await getAllComments();
      if (all) setComments(all);
    };

    fetchUsers();
    fetchComments();
  }, [])

  const transformedComments = comments.map((c: any) => {
    const pageMatch = c.link.match(/\.com\/([^#]+)/);
    const page = pageMatch && pageMatch[1] ? decodeURIComponent(pageMatch[1]) : "Unknown";
    return {
      id: c.id,
      author: c.author_name || "Anonymous",
      avatar: c.author_avatar_urls?.[48] || "/default-avatar.png",
      email: "", // Email is not available in public comment JSON
      content: c.content?.rendered?.replace(/<[^>]+>/g, "") ?? "",
      ministry: page,
      title: c.meta.testimonial_title,
      likes: 0,
      status: c.status,
      createdAt: new Date(c.date),
      isPinned: false,
      replies: 0, // Adjust if you plan to load reply count later
      original: c,
    };
  });

  const filteredComments = useMemo(() => {
    return transformedComments.filter((c: any) => c.ministry !== "2025/02/10/hello-world/");
  }, [transformedComments]);
  // const pinnedTestimonials = testimonials.filter(t => t.isPinned).slice(0, 2);
  const pinnedTestimonials = filteredComments.slice(0, 2); ////Check the array for pinned testimonials after it is implemented

  return (
    <div className="min-h-screen flex justify-center items-center px-4 py-16 relative overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1054218/pexels-photo-1054218.jpeg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent"></div>
      </div>

      {/* Floating Elements */}
      <motion.div
        animate={{
          y: [-20, 20, -20],
          rotate: [0, 10, 0, -10, 0]
        }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-20 left-10 hidden lg:block"
      >
        <Quote className="h-12 w-12 text-rose-300/60" />
      </motion.div>

      <motion.div
        animate={{
          y: [15, -15, 15],
          rotate: [0, -8, 0, 8, 0]
        }}
        transition={{ duration: 6, repeat: Infinity, delay: 1 }}
        className="absolute top-32 right-16 hidden lg:block"
      >
        <Heart className="h-10 w-10 text-pink-400/60" />
      </motion.div>

      <motion.div
        animate={{
          y: [-10, 10, -10],
          x: [-5, 5, -5]
        }}
        transition={{ duration: 7, repeat: Infinity, delay: 2 }}
        className="absolute bottom-32 left-20 hidden lg:block"
      >
        <Sparkles className="h-8 w-8 text-purple-400/60" />
      </motion.div>

      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative z-10 mx-auto max-w-7xl w-full"
      >
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="inline-block mb-6"
          >
            <div className="bg-gradient-to-r from-rose-500 to-pink-500 p-4 rounded-2xl shadow-lg">
              <MessageCircle className="h-12 w-12 text-white" />
            </div>
          </motion.div>

          <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Life-Changing Testimonies
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover how consistent Christ-centered Bible study and prayer are transforming lives and strengthening faith communities around the world.
          </p>

          {/* Stats */}
          <motion.div
            className="flex flex-wrap justify-center gap-8 mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg">
              <Users className="h-5 w-5 text-rose-500 mr-2" />
              <span className="font-semibold text-gray-700">{allUsers.length}+ Members</span>
            </div>
            <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg">
              <TrendingUp className="h-5 w-5 text-pink-500 mr-2" />
              <span className="font-semibold text-gray-700">100% Christo-centric</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {pinnedTestimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0 + index * 0.2 }}
              // whileHover={{ y: -10, scale: 1.02, transition: { type: "spring", stiffness: 300, damping: 20 } }}
              className="group relative"
            >
              {/* Card Background with Gradient Border */}
              <div className="absolute inset-0 bg-gradient-to-r from-rose-400 via-pink-400 to-purple-400 rounded-3xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>

              <div className="relative bg-white/90 backdrop-blur-lg rounded-3xl p-8 shadow-xl border border-white/50 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-100 to-pink-100 rounded-full blur-2xl opacity-50"></div>

                {/* Quote Icon */}
                <motion.div
                  animate={{ rotate: [0, 5, 0, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity, delay: index }}
                  className="absolute top-6 right-6"
                >
                  <Quote className="h-8 w-8 text-rose-300" />
                </motion.div>

                {/* Author Info */}
                <div className="flex items-center mb-6">
                  <motion.div
                    whileHover={{ scale: 1.1, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                    className="relative"
                  >
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.author}
                      className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full p-1">
                      <Star className="h-4 w-4 text-white" fill="currentColor" />
                    </div>
                  </motion.div>

                  <div className="ml-4 flex-1">
                    <h4 className="font-bold text-xl text-gray-800">{testimonial.author}</h4>
                    <p className="text-rose-600 font-medium">
                      {testimonial.ministry
                        .replace(/\/|-/g, " ")
                        .replace(/\b\w/g, (char) => char.toUpperCase())}
                    </p>
                    <div className="flex items-center mt-1">
                      <div className="flex items-center text-pink-500 mr-4">
                        <Heart className="h-4 w-4 mr-1" fill="currentColor" />
                        <span className="text-sm font-semibold">{testimonial.likes}</span>
                      </div>
                      <span className="bg-gradient-to-r from-rose-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                        FEATURED
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-900 leading-relaxed text-lg mb-1 line-clamp-6 font-extrabold">{testimonial.title}</p>

                {/* Testimonial Content */}
                <motion.blockquote
                  className="text-gray-700 leading-relaxed text-md mb-6 relative z-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 + index * 0.2 }}
                >
                  {/* "{testimonial.content}" */}
                  <div
                    className="text-gray-700"
                    dangerouslySetInnerHTML={{ __html: testimonial.content }}
                  />
                </motion.blockquote>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-gray-500 text-sm">
                    {testimonial.createdAt.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: index }}
                  >
                    <Sparkles className="h-5 w-5 text-purple-400" />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/testimonials"
              className="inline-flex items-center bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 text-white px-10 py-5 rounded-full font-bold text-lg shadow-2xl hover:shadow-rose-500/25 transition-all duration-300 group"
            >
              <span>View All Stories & Share Yours</span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="ml-3"
              >
                <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </motion.div>
            </Link>
          </motion.div>

          <p className="text-gray-500 mt-4 text-sm">
            Join thousands sharing their transformation stories
          </p>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default TestimonialsSection;